import { clsx } from 'clsx'
import { PropertyValueMap, TemplateResult, unsafeCSS } from 'lit'
import { property, query } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html, unsafeStatic } from 'lit/static-html.js'

import type { AccordionVariant } from '@/common/types'
import { HeadingLevel, LabelPlacement } from '@/dap-design-system'
import { DdsElement } from '@/internal/dds-hu-element'
import { SizedMixin } from '@/internal/mixin/sizedMixin'
import { t } from '@/localization/localization'

import type { IconSize } from '../icon/icon.component'
import styles from './accordion.scss?inline'

export interface AccordionEventDetail {
  open: boolean
  item: AccordionBaseElement
}

export type AccordionOpenedEvent = CustomEvent<AccordionEventDetail>
export type AccordionClosedEvent = CustomEvent<AccordionEventDetail>

const ACCORDION_VARIANTS = [
  'default',
  'collapsed',
  'clean',
  'clean-collapsed',
] as const

const AccordionBaseWithSized = SizedMixin(DdsElement)

export class AccordionBaseElement extends AccordionBaseWithSized {
  static readonly styles = unsafeCSS(styles)
  private static nextId = 0

  private readonly accordionId: string
  private readonly buttonId: string
  private readonly contentId: string

  private _headingLevel: HeadingLevel = 4
  private _variant: AccordionVariant = 'default'

  /** Query for the accordion button element */
  @query('[part="button"]')
  protected button?: HTMLButtonElement

  /** Query for the accordion content element */
  @query('[part="content"]')
  protected content?: HTMLDivElement

  /** Display size for accordion styling (sm or lg); other effective sizes map to sm. */
  private get accordionDisplaySize(): 'sm' | 'lg' {
    return this.effectiveSize === 'lg' ? 'lg' : 'sm'
  }

  /** The heading level of the accordion
   * @type { 1 | 2 | 3 | 4 | 5 | 6 }
   */
  @property({ type: Number })
  get headingLevel(): HeadingLevel {
    return this._headingLevel
  }
  set headingLevel(value: HeadingLevel) {
    const oldValue = this._headingLevel
    if (value < 1 || value > 6) {
      console.warn('headingLevel must be between 1 and 6, using default of 4')
      this._headingLevel = 4
    } else {
      this._headingLevel = value
    }
    this.requestUpdate('headingLevel', oldValue)
  }

  /** Whether the accordion is opened */
  @property({ type: Boolean, reflect: true }) opened = false

  /** Whether the accordion is disabled */
  @property({ type: Boolean, reflect: true }) disabled = false

  /** Whether the accordion is in loading state */
  @property({ type: Boolean, reflect: true }) loading = false

  /** The heading text of the accordion, this will be used as the aria label of the heading also if ariaLabel is not provided */
  @property({ type: String }) heading?: string

  /** The loading aria label of the accordion */
  @property({ type: String }) loadingAriaLabel?: string

  /** The location of the icon
   * @type { 'left' | 'right' }
   */
  @property({ type: String }) iconLocation: LabelPlacement = 'right'

  /** The variant of the accordion
   * @type { 'default' | 'collapsed' | 'clean' | 'clean-collapsed' }
   */
  @property({ type: String })
  get variant(): AccordionVariant {
    return this._variant
  }
  set variant(value: AccordionVariant) {
    const oldValue = this._variant
    if (!ACCORDION_VARIANTS.includes(value as any)) {
      console.warn(`Invalid accordion variant: ${value}. Using default.`)
      this._variant = 'default'
    } else {
      this._variant = value
    }
    this.requestUpdate('variant', oldValue)
  }

  /** Whether this is the last item in an accordion group */
  @property({ type: Boolean }) lastItem = false

  constructor() {
    super()
    AccordionBaseElement.nextId++
    const uniqueSuffix =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID().slice(0, 8)
        : Date.now().toString(36)
    this.accordionId = `accordion-${AccordionBaseElement.nextId}-${uniqueSuffix}`
    this.buttonId = `${this.accordionId}-button`
    this.contentId = `${this.accordionId}-content`
  }

  /**
   * Renders the heading content. Override in subclasses to customize heading rendering.
   * @returns Template result for heading content
   */
  protected headingRender() {
    return html`<div>${this.heading}</div>`
  }

  /**
   * Renders additional content. Override in subclasses to add custom content.
   * @returns Template result for additional content
   */
  protected contentRender() {
    return html`<div></div>`
  }

  validateProperties(): void {
    if (this.headingLevel < 1 || this.headingLevel > 6) {
      console.warn('headingLevel must be between 1 and 6')
    }

    if (!ACCORDION_VARIANTS.includes(this.variant as any)) {
      console.warn(`Invalid accordion variant: ${this.variant}. Using default.`)
    }
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)
    this.validateProperties()
  }

  private onChange(): void {
    const eventName = this.opened ? 'dds-opened' : 'dds-closed'
    const detail: AccordionEventDetail = {
      open: this.opened,
      item: this,
    }

    this.emit(eventName, detail)
  }

  private toggle(): void {
    if (this.disabled || this.loading) {
      return
    }

    this.opened = !this.opened
    this.onChange()
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (this.disabled || this.loading) {
      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      this.toggle()
    }
  }

  private handleClick = (event: Event): void => {
    if (this.disabled || this.loading) {
      event.preventDefault()
      return
    }

    this.toggle()
  }

  private get accordionClasses() {
    return clsx('accordion', {
      'accordion--opened': this.opened,
      'accordion--disabled': this.disabled,
      'accordion--loading': this.loading,
      'accordion--sm': this.accordionDisplaySize === 'sm',
      'accordion--lg': this.accordionDisplaySize === 'lg',
      'accordion--icon-left': this.iconLocation === 'left',
      'accordion--icon-right': this.iconLocation === 'right',
      [`accordion--${this.variant}`]: this.variant,
      'accordion--last-item': this.lastItem,
    })
  }

  private renderIconOrSpinner() {
    if (this.loading) {
      return html`
        <span
          class="accordion__loading"
          role="status"
          aria-live="polite"
          aria-label="${this.loadingAriaLabel ?? t('loading')}">
          <dap-ds-icon-loading-spinner size="16"></dap-ds-icon-loading-spinner>
        </span>
      `
    }

    return html`
      <span
        class="accordion__icon-wrapper"
        aria-hidden="true"
        part="icon-wrapper">
        <slot name="icon-opened" class="accordion__icon-opened">
          <dap-ds-icon
            exportparts="icon:open-icon, base:open-icon-base"
            size=${(this.effectiveSize === 'xxs'
              ? 'xs'
              : this.effectiveSize) as IconSize}
            name="arrow-up-s-line"
            class="open-icon">
          </dap-ds-icon>
        </slot>
        <slot name="icon-closed" class="accordion__icon-closed">
          <dap-ds-icon
            exportparts="icon:close-icon, base:close-icon-base"
            size=${(this.effectiveSize === 'xxs'
              ? 'xs'
              : this.effectiveSize) as IconSize}
            name="arrow-down-s-line"
            class="close-icon">
          </dap-ds-icon>
        </slot>
      </span>
    `
  }

  private renderButtons() {
    return html`
      <button
        class="accordion__button"
        type="button"
        part="button"
        id="${this.buttonId}"
        aria-controls="${this.contentId}"
        aria-expanded="${this.opened}"
        aria-disabled="${this.disabled}"
        aria-label=${ifDefined(this.ariaLabel ?? this.heading)}
        aria-describedby=${ifDefined(this.ariaDescribedBy ?? undefined)}
        ?disabled=${this.disabled}
        @click=${this.handleClick}
        @keydown=${this.handleKeyDown}>
        ${this.renderIconOrSpinner()}
        <span part="heading-text" class="accordion__heading-text">
          <slot name="heading"> ${this.headingRender()} </slot>
        </span>
      </button>
    `
  }

  private renderContent() {
    return html`
      <div
        part="content"
        class="accordion__content"
        id="${this.contentId}"
        aria-labelledby="${this.buttonId}"
        role="region"
        aria-hidden="${!this.opened}">
        <div part="content-container" class="accordion-content__container">
          <slot></slot>
          ${this.contentRender()}
        </div>
      </div>
    `
  }

  private renderHeading(): TemplateResult {
    return html`
    <div class="accordion__heading" part="heading">
      <${unsafeStatic(`h${this.headingLevel}`)}
       class="accordion__heading-content">
        ${this.renderButtons()}
      </${unsafeStatic(`h${this.headingLevel}`)}>
  </div>
    `
  }

  /**
   * Renders the complete accordion component. Override in subclasses to customize the entire accordion structure.
   * @returns Template result for the complete accordion
   */
  renderAccordion() {
    return html`
      <div part="base" class=${this.accordionClasses}>
        ${this.renderHeading()} ${this.renderContent()}
      </div>
    `
  }
}
